import { Panel, Spinner } from '@ixicare/ui';
import React from 'react';
import connect from '~store/connect';
import { State } from '~@types/store';
import { useDeviceHealthDataFormatter } from '../components/DeviceHealthTable/useDeviceHealthDataFormatter';
import { useDeviceHealthTables } from '../components/DeviceHealthTable/useDeviceHealthTable';
import { Status } from '../components/DeviceHealthTable/types';
import { DeviceHealthTable } from '../components/DeviceHealthTable';
import { t } from '~/i18n';
import styles from './DeviceHealthContainer.module.css';
import { useSelectedSite } from '~/hooks/useSelectedSite';
import { GenericTable } from '~components/Tables/DeviceTables/GenericTable';
import { BaseTableConfig } from '~components/Tables/DeviceTables/types';

interface DeviceHealthContainerProps {
  loadSites: () => void;
  sitesLoading: boolean;
}

const DeviceHealthContainer: React.FC<DeviceHealthContainerProps> = ({ loadSites, sitesLoading }) => {
  React.useEffect(() => {
    loadSites();
  }, [loadSites]);

  const [selectedSite] = useSelectedSite();

  const { data, isLoading } = useDeviceHealthTables({ site: selectedSite });

  const { summaryRow, outputStatuses, handleSort, orderBy, ascOrder } = useDeviceHealthDataFormatter({
    data,
    site: selectedSite,
  });

  const scrollToStatus = (status: string) => {
    const element = document.getElementById(`status-${status.toLowerCase().replace(/_/g, '-')}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (sitesLoading) {
    return <Spinner />;
  }

  const statusValues = Object.values(Status);
  const columns = statusValues.map((x) => t(`device-health.status.${x.toLowerCase()}.name`));

  const summaryConfig: BaseTableConfig = {
    tableId: 'device-health-summary',
    columns,
    columnIds: statusValues,
  };

  return (
    <>
      {selectedSite && (
        <div className="print-panel">
          <Panel>
            {isLoading ? (
              <Spinner />
            ) : !outputStatuses.length ? (
              <Panel>
                <Panel.Title>{t('device-health.no-devices.title')}</Panel.Title>
                <p>{t('device-health.no-devices.description')}</p>
              </Panel>
            ) : (
              <>
                <Panel>
                  <Panel.Title>{t('device-health.summary')}</Panel.Title>
                  <GenericTable config={summaryConfig} rows={summaryRow} onClickColumn={scrollToStatus} />
                </Panel>
                {outputStatuses.flatMap((panelRow) =>
                  panelRow.map(([status, content]) => (
                    <div
                      key={status}
                      id={`status-${status.toLowerCase().replace(/ /g, '-')}`}
                      className={styles.deviceTableSection}
                    >
                      <DeviceHealthTable
                        config={content.config}
                        status={status}
                        rows={content.data}
                        orderBy={orderBy}
                        ascOrder={ascOrder}
                        handleSort={handleSort}
                      />
                    </div>
                  ))
                )}
              </>
            )}
          </Panel>
        </div>
      )}
    </>
  );
};

export default connect((state: State) => ({
  loadSites: state.sites.loadSites,
  sitesLoading: state.sites.loading,
}))(DeviceHealthContainer);
